import React, { useState, useEffect } from 'react'
import moment from 'moment'

import { FragmentType, graphql, useFragment } from '@/generated/gql'
import {
  Dialog,
  Stack,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material'
import { HourglassTop, Info } from '@mui/icons-material'
import Navbar from './Navbar'
import MenuCart from './MenuCart'
import MenuHeader from './MenuHeader'
import MenuBody from './MenuBody'
import ItemRating from './ItemRating'
import { useCartState } from './MenuCart/useCartState'
import { Meal } from '@/generated/gql/graphql'
import { usePlaceCanteenOrder } from './state/usePlaceCanteenOrder'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'

const canteenMenuViewCurrentMenuDataFragment = graphql(`
  fragment CanteenMenuView_CurrentMenuDataFragment on CCMealService {
    ...CanteenMenuCart_MenuDataFragment
    ...CanteenMenuHeader_CurrentMenuDataFragment
    availableSubsidy
    openTime
  }
`)

const canteenMenuViewUserDataFragment = graphql(`
  fragment CanteenMenuView_UserDataFragment on User {
    ...CanteenMenuBody_UserDataFragment
    ...CanteenMenuHeader_UserDataFragment
    ...CanteenMenuCart_UserDataFragment
    ...CanteenNavbar_UserDataFragment
    ...CanteenOrderedItems_UserDataFragment
    ...CanteenMenuItems_UserDataFragment
    subsidySettings {
      unlimitedSubsidy
    }
    wallet {
      balance
    }
    account {
      id
    }
  }
`)

type Props = {
  meal: string
  date: string
  timeslot?: string | null
  currency: string
  userData: FragmentType<typeof canteenMenuViewUserDataFragment>
  menuData?: FragmentType<typeof canteenMenuViewCurrentMenuDataFragment> | null
  menuFilters: { dietaries: string[]; allergens: string[]; vendors: string[] }
  menuClosed: boolean
  language: string
  orderTimeRemaining: moment.Duration
  loadingSelectedServiceData: boolean
  onClearFilters: () => void
  onToggleFilterValue: (category: 'vendors' | 'allergens' | 'dietaries', value: string) => void
  onChangeSelectedService: (input: { meal: string; date: string }) => void
  onChangeAccountLocation: (accountLocationId: number) => void
  onOrderPlacement: (data: { success: boolean; forwardUrl: string }) => void
  reloadFromApi: () => Promise<void>
  onChangeTimeslot: (timeslot: string) => void
}

const MenuView: React.FC<Props> = ({
  date,
  meal,
  timeslot,
  language,
  userData: userDataProp,
  menuData: menuDataProp,
  menuClosed,
  orderTimeRemaining,
  currency,
  menuFilters,
  loadingSelectedServiceData,
  onClearFilters,
  onOrderPlacement,
  reloadFromApi,
  onChangeSelectedService,
  onChangeAccountLocation,
  onChangeTimeslot,
  onToggleFilterValue,
}) => {
  const userData = useFragment(canteenMenuViewUserDataFragment, userDataProp)
  const useSynchronousOrderPlacement = useFeatureFlag(SPLITS.USE_SYNCHRONOUS_CC_ORDER_PLACEMENT, {
    accountId: userData.account?.id ?? '',
  })
  const {
    addCartItem,
    updateCartItem,
    removeCartItem,
    emptyCart,
    cartItems,
    cartTotal,
    itemQuantity: cartItemQuantity,
  } = useCartState()
  const {
    placeOrder,
    error: placeOrderError,
    data: placeOrderData,
    loading: placingOrder,
  } = usePlaceCanteenOrder({ useSynchronousOrderPlacement })

  const menuData = useFragment(canteenMenuViewCurrentMenuDataFragment, menuDataProp)
  const [orderAtttemptQty, setOrderAttemptQty] = useState(0)
  const [showCancelBasketModal, setShowCancelBasketModal] = useState(false)
  const [showSideBar, setShowSideBar] = useState(false)
  const [errorMessageClosed, setErrorMessageClosed] = useState(false)
  const [basketEmptyConfirmationCallback, setBasketEmptyConfirmationCallback] = useState<
    (() => void) | null
  >(null)

  useEffect(() => {
    if (cartItemQuantity === 0 && showSideBar) {
      setShowSideBar(false)
    }
  }, [cartItemQuantity, showSideBar])

  useEffect(() => {
    if (cartItemQuantity === 0) return
    if (timeslot && menuClosed) emptyCart()
  }, [menuClosed, timeslot, emptyCart, cartItemQuantity])

  const handlePlaceOrder = async (bundle: number) => {
    if (!timeslot || menuClosed) {
      return
    }
    await placeOrder({
      language,
      bundle,
      items: cartItems,
      arrivalTime: timeslot,
      cartTotal,
      currency,
      balance: userData.wallet.balance ?? 0,
      subsidy: menuData?.availableSubsidy ?? 0,
      unlimitedSubsidy: Boolean(userData.subsidySettings.unlimitedSubsidy),
      validDate: date,
      validMeal: meal,
    })
  }

  useEffect(() => {
    if (!placeOrderData) return
    onOrderPlacement({ success: true, forwardUrl: placeOrderData })
  }, [placeOrderData, onOrderPlacement])

  useEffect(() => {
    if (!placeOrderError) return
    setOrderAttemptQty((prev) => prev + 1)
    setErrorMessageClosed(false)
    onOrderPlacement({ success: false, forwardUrl: '' })
  }, [placeOrderError, onOrderPlacement])

  const onNavChange = (callback: () => void) => {
    if (cartItems && cartItems.length > 0) {
      setShowCancelBasketModal(true)
      setBasketEmptyConfirmationCallback(() => callback)
    } else {
      callback()
    }
  }

  const cancelBasketModalClicked = () => {
    setShowCancelBasketModal(false)
    if (basketEmptyConfirmationCallback) {
      emptyCart()
      basketEmptyConfirmationCallback()
    }
  }

  const cancelBasketModalClosed = () => {
    setShowCancelBasketModal(false)
  }

  const handleShowSideBar = () => {
    setShowSideBar(true)
  }

  const closeSideBar = () => {
    if (placingOrder) return
    setShowSideBar(false)
  }

  const handleCloseOrderErrorMessage = () => {
    setErrorMessageClosed(true)
  }

  const openingTime = menuData?.openTime

  return (
    <>
      <Dialog onClose={() => cancelBasketModalClosed()} open={showCancelBasketModal}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Info color="warning" />
            <Typography>Changing day or meal will empty your cart</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack paddingY={1}>
            <Typography>
              You can’t order for multiple days or meals at a time. If you want to change, your cart
              will be emptied. If that’s ok, empty your cart now.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => cancelBasketModalClosed()}>
            Continue ordering
          </Button>
          <Button
            onClick={() => {
              cancelBasketModalClicked()
            }}
          >
            Empty my cart
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(!errorMessageClosed && placeOrderError)}
        onClose={handleCloseOrderErrorMessage}
      >
        <Alert
          onClose={handleCloseOrderErrorMessage}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {placeOrderError}
        </Alert>
      </Snackbar>

      {menuData && timeslot ? (
        <MenuCart
          placingOrder={Boolean(placingOrder || placeOrderData)}
          currency={currency}
          onRemove={removeCartItem}
          open={showSideBar}
          userData={userData}
          menuData={menuData}
          cartItems={cartItems}
          cartTotal={cartTotal}
          onClose={closeSideBar}
          onUpdate={updateCartItem}
          placeOrder={handlePlaceOrder}
          onChangeTimeslot={onChangeTimeslot}
          arrivalTime={timeslot}
        />
      ) : null}

      <ItemRating />
      <Navbar
        onCartPreviewClick={() => handleShowSideBar()}
        userData={userData}
        cartTotal={cartTotal}
        cartItemQuantity={cartItemQuantity}
      />

      <MenuHeader
        onChangeTimeslot={onChangeTimeslot}
        onChangeAccountLocation={(accountLocationId) => {
          onNavChange(() => {
            onChangeAccountLocation(accountLocationId)
          })
        }}
        onChangeSelectedService={(input) => {
          onNavChange(() => {
            onChangeSelectedService(input)
          })
        }}
        currency={currency}
        userData={userData}
        menuData={menuData}
        meal={meal}
        isClosed={menuClosed}
        openingTime={openingTime}
        date={date}
        arrivalTime={timeslot}
        orderTimeRemaining={orderTimeRemaining}
      />

      {timeslot && menuData ? (
        <MenuBody
          ordersAttempted={orderAtttemptQty}
          onClearFilters={onClearFilters}
          menuFilters={menuFilters}
          onToggleFilterValue={onToggleFilterValue}
          addToCart={(item) => {
            if (placingOrder) return
            addCartItem(item, timeslot)
            handleShowSideBar()
          }}
          cartItems={cartItems.map((item) => item.id)}
          onSuccessfulCancellation={reloadFromApi}
          userData={userData}
          date={date}
          meal={meal as Meal}
          timeslot={timeslot}
        />
      ) : (
        <>
          {loadingSelectedServiceData ? null : (
            <Stack paddingTop={2} direction="column" spacing={2} width="100%" alignItems="center">
              <HourglassTop color="info" sx={{ height: 60, width: 60 }} />
              <Typography variant="h5">There seems to be no menu data for this service.</Typography>
              <Typography variant="body1">Things may not be set up quite yet.</Typography>
            </Stack>
          )}
        </>
      )}
    </>
  )
}

export default MenuView
